import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import EditableText from "@/components/editor/EditableText";
import { cdn, heroSrcSet } from "@/lib/img";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sunset whale image (user-provided) leads the fallback set.
const SUNSET_WHALE_IMG = "https://customer-assets-wrfwihn1.emergentagent.net/job_whale-cove-retreat/artifacts/r7abrnsz_image.png";

// Fallback set — used only if the admin has zero photos tagged section=hero in the DB.
// Guarantees the hero never renders blank.
const FALLBACK_HERO_IMAGES = [
  { src: SUNSET_WHALE_IMG, alt: "Sunset over the Bay of Fundy with a minke whale surfacing — Whale Cove Off Grid, Digby Neck NS" },
  { src: "https://customer-assets.emergentagent.com/job_whale-cove-retreat/artifacts/z88nvx3c_IMG_7573.jpeg", alt: "Rocky shoreline and forest at Whale Cove" },
  { src: "https://customer-assets.emergentagent.com/job_whale-cove-retreat/artifacts/iv6etzg7_IMG_8819.jpeg", alt: "Misty rocky coast with spruce trees, Bay of Fundy" },
  { src: "https://customer-assets.emergentagent.com/job_whale-cove-retreat/artifacts/n4fwk8zv_IMG_8775.jpeg", alt: "Fishing boat on the Bay of Fundy, rocky shore in foreground" },
  { src: "https://customer-assets.emergentagent.com/job_whale-cove-retreat/artifacts/3ivlhgej_IMG_8910.jpeg", alt: "Trail between towering rock faces at Whale Cove" },
  { src: "https://customer-assets.emergentagent.com/job_whale-cove-retreat/artifacts/a0c28b9l_IMG_8914.JPG", alt: "Moss-covered bedrock and forest along the trail" },
  { src: "https://customer-assets.emergentagent.com/job_whale-cove-retreat/artifacts/je5ne52t_IMG_8958.jpeg", alt: "Rocky shoreline looking along the Bay of Fundy coast" },
  { src: "https://customer-assets.emergentagent.com/job_whale-cove-retreat/artifacts/no751j3x_IMG_8970.jpeg", alt: "Forested headland meeting the Bay of Fundy" },
];

const INTERVAL = 7000;

export default function Hero() {
  const [images, setImages] = useState(FALLBACK_HERO_IMAGES);
  const [current, setCurrent] = useState(0);
  const [heroLoaded, setHeroLoaded] = useState(false);

  // Fetch admin-managed hero images. Only replace fallback if we actually got some back.
  useEffect(() => {
    axios
      .get(`${API}/photos/public`, { params: { section: "hero" } })
      .then((res) => {
        const dbPhotos = (res.data?.photos || [])
          .filter((p) => p && (p.id || p.url) && (p.section === "hero" || !p.section))
          .map((p) => ({
            // Photos in the object store are served via the backend proxy endpoint.
            // Direct-URL photos (rare/legacy) fall through as-is.
            src: p.url || `${API}/photos/${p.id}/image`,
            alt: p.label || p.caption || "Whale Cove Off Grid — Bay of Fundy",
          }));
        if (dbPhotos.length > 0) setImages(dbPhotos);
      })
      .catch(() => { /* silently fall through to hardcoded fallback */ });
  }, []);

  const next = useCallback(() => {
    setCurrent((prev) => (prev + 1) % images.length);
  }, [images.length]);

  useEffect(() => {
    const timer = setInterval(next, INTERVAL);
    return () => clearInterval(timer);
  }, [next]);

  // If image count changes (DB fetched), reset to first slide
  useEffect(() => { setCurrent(0); }, [images.length]);

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative h-screen min-h-[600px] flex items-center justify-center overflow-hidden"
    >
      {/* Preload the NEXT hero image — only after the current one has loaded,
          so it doesn't steal bandwidth from first paint. */}
      {heroLoaded && images[(current + 1) % images.length] && (
        <img
          src={cdn(images[(current + 1) % images.length].src, 1100)}
          alt=""
          aria-hidden="true"
          className="absolute w-0 h-0 opacity-0 pointer-events-none"
          loading="lazy"
          decoding="async"
        />
      )}

      {/* Slideshow background */}
      <div className="absolute inset-0">
        <AnimatePresence mode="sync">
          <motion.img
            key={current}
            src={cdn(images[current].src, 1100)}
            srcSet={heroSrcSet(images[current].src)}
            sizes="100vw"
            alt={images[current].alt}
            fetchpriority={current === 0 ? "high" : "auto"}
            decoding="async"
            onLoad={() => setHeroLoaded(true)}
            className="absolute inset-0 w-full h-full object-cover"
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.8, ease: "easeInOut" }}
          />
        </AnimatePresence>
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/60 z-[1]" />
      </div>

      {/* Content */}
      <div className="relative z-10 text-center px-6 max-w-6xl mx-auto">
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="font-display text-white text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold leading-tight mb-6 lg:whitespace-nowrap"
        >
          Where the forest meets the Bay...
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="font-accent italic font-semibold text-white text-2xl sm:text-3xl mb-3"
        >
          <EditableText contentKey="hero_tagline" as="span" className="" defaultValue="Your private piece of the Bay of Fundy." />
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.45 }}
          className="font-accent italic font-semibold text-white text-xl sm:text-2xl mb-10 lg:whitespace-nowrap"
        >
          <EditableText contentKey="hero_subtitle" as="span" className="" defaultValue="Exclusive whole-property rental. One site, up to 3 trailers, very private." />
        </motion.p>
      </div>

      {/* Slide indicators */}
      <div className="absolute bottom-20 sm:bottom-24 left-1/2 -translate-x-1/2 z-10 flex gap-2">
        {images.map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrent(i)}
            data-testid={`hero-slide-${i}`}
            className={`w-2 h-2 rounded-full transition-all duration-500 ${
              i === current ? "bg-white w-6" : "bg-white/40 hover:bg-white/60"
            }`}
          />
        ))}
      </div>

      {/* Animated wave at bottom */}
      <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-[0] z-[2]">
        <svg
          viewBox="0 0 1440 80"
          className="w-full h-[50px] sm:h-[70px]"
          preserveAspectRatio="none"
        >
          <motion.path
            d="M0,40 C360,80 720,0 1080,40 C1260,60 1380,50 1440,40 L1440,80 L0,80 Z"
            fill="#E4E4E0"
            initial={{ d: "M0,50 C360,70 720,10 1080,50 C1260,65 1380,55 1440,50 L1440,80 L0,80 Z" }}
            animate={{
              d: [
                "M0,40 C360,80 720,0 1080,40 C1260,60 1380,50 1440,40 L1440,80 L0,80 Z",
                "M0,50 C360,20 720,70 1080,50 C1260,35 1380,45 1440,50 L1440,80 L0,80 Z",
                "M0,40 C360,80 720,0 1080,40 C1260,60 1380,50 1440,40 L1440,80 L0,80 Z",
              ],
            }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          />
        </svg>
      </div>
    </section>
  );
}
